// src/theme/useAppearance.js
// Light or dark (3.45.0) and the colour theme (3.46.0). Each person picks
// Auto, Light or Dark (Preferences, or the quick switch in your menu); Auto
// follows the computer's own setting and changes with it. The theme is their
// own pick, else their client's starting theme, else Teal (theme/themes.js).
// Both are saved to their account with their other preferences, so they
// follow them to any computer.
//
// <html data-mode="dark"> and <html data-theme="ocean|plum"> switch the
// colours (theme/palette.css). They are only set while the portal itself is
// showing: the sign-in pages always keep their own light look.
import { useEffect, useLayoutEffect, useState } from 'react';

export const APPEARANCES = [
  { id: 'light', label: 'Light', icon: '☀️', hint: 'Always light' },
  { id: 'auto',  label: 'Auto',  icon: '🌓', hint: "Matches your computer's own light or dark setting" },
  { id: 'dark',  label: 'Dark',  icon: '🌙', hint: 'Always dark' },
];

const QUERY = '(prefers-color-scheme: dark)';
const systemDark = () => typeof window !== 'undefined' && !!window.matchMedia?.(QUERY).matches;

// The mode actually showing for a choice ('auto' | 'light' | 'dark')
export function useShownMode(choice) {
  const [sysDark, setSysDark] = useState(systemDark);
  useEffect(() => {
    const mq = window.matchMedia?.(QUERY);
    if (!mq) return undefined;
    const onChange = () => setSysDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return choice === 'dark' || (choice !== 'light' && sysDark) ? 'dark' : 'light';
}

// 3.47.1: the last look shown in this browser, so the "Opening your Fax
// Portal…" screen after a refresh already has it (before the account loads)
const LOOK_KEY = 'faxPortal.look';
export function lastLook() {
  try {
    const v = JSON.parse(window.localStorage.getItem(LOOK_KEY) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch { return null; }
}

export function useAppearance(choice, active, theme = 'teal') {
  const mode = useShownMode(choice);
  useLayoutEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    root.dataset.mode = mode;
    root.dataset.theme = theme;
    try { window.localStorage.setItem(LOOK_KEY, JSON.stringify({ choice, theme })); } catch { /* ignore */ }
    return () => { delete root.dataset.mode; delete root.dataset.theme; };
  }, [mode, active, theme, choice]);
  return mode;
}

// The loading screen while a signed-in person's portal opens: the look they
// last had here (Auto still follows the computer). Removed again when it
// closes, so the sign-in pages keep their own light look.
export function useLoadingLook() {
  const look = lastLook();
  const mode = useShownMode(look?.choice || 'auto');
  useLayoutEffect(() => {
    if (!look) return undefined;
    const root = document.documentElement;
    if (root.dataset.theme) return undefined;     // the portal has set it already
    root.dataset.mode = mode;
    root.dataset.theme = look.theme || 'teal';
    return () => { delete root.dataset.mode; delete root.dataset.theme; };
  }, [mode, look?.theme]);   // eslint-disable-line react-hooks/exhaustive-deps
}
